import { assocPath, compose, equals, init, split } from 'ramda';

import type { ZodType } from 'zod';

/**
 * Converts a field name to a path array.
 * Supports both dot notation ('address.street') and simple names ('email').
 * @param fieldName - The field name (can be dot-notated or simple)
 * @returns Array representing the path
 */
const fieldNameToPath = compose<[string | number | symbol], string, string[]>(split('.'), String);

/**
 * Builds a test object with empty parent objects for nested paths.
 * For 'address.street', returns { address: {} }
 * For 'email', returns {}
 * This allows Zod to report errors for the nested field itself, not just the parent.
 */
const buildTestObject = (fieldPath: (string | number)[]): Record<string, unknown> =>
  fieldPath.length === 1 ? {} : assocPath(init(fieldPath), {}, {});

/**
 * Determines if a field is required in a Zod schema.
 * Supports nested fields using dot notation (e.g., 'address.street').
 * @param schema - The Zod schema to inspect
 * @param fieldName - The field name to check (supports dot notation for nested fields)
 * @returns boolean indicating if the field is required
 */
export function isFieldRequired<T extends Record<string, unknown>>(schema: ZodType<T>, fieldName: keyof T): boolean {
  try {
    // Convert field name to path array for comparison
    const fieldPath = fieldNameToPath(fieldName);

    // Build test object with parent structure for nested fields
    const testObject = buildTestObject(fieldPath);

    // Try to parse and see if the field causes an error
    const result = schema.safeParse(testObject as T);

    if (result.success) {
      return false;
    }

    // Check if there's an error for this specific field
    const fieldError = result.error.issues.find((issue) => equals(issue.path, fieldPath));

    return !!fieldError;
  } catch {
    // If we can't determine, assume it's not required
    return false;
  }
}

/**
 * Gets required status for all fields in a schema.
 * Supports nested field names using dot notation (e.g., 'address.street').
 * @param schema - The Zod schema to inspect
 * @param fieldNames - Array of field names to check (supports dot notation)
 * @returns Map of field names to their required status
 */
export function getFieldsRequiredStatus<T extends Record<string, unknown>>(
  schema: ZodType<T>,
  fieldNames: (keyof T)[]
): Map<keyof T, boolean> {
  const requiredMap = new Map<keyof T, boolean>();

  fieldNames.forEach((fieldName) => {
    requiredMap.set(fieldName, isFieldRequired(schema, fieldName));
  });

  return requiredMap;
}
